import { useCallback, useEffect, useState } from 'react';

interface UseLinkShortenerProps {
  editor: any;
  excludedClearTrigger?: number;
  onProcessingChange?: (isProcessing: boolean) => void;
}

export function useLinkShortener({ editor, excludedClearTrigger, onProcessingChange }: UseLinkShortenerProps) {
  const [isProcessing, setIsProcessing] = useState(false);

  const getStorage = useCallback(() => {
    return editor?.storage?.link; // Storage is keyed by extension name ('link')
  }, [editor]);

  const addExcludedUrl = useCallback((url: string) => {
    const storage = getStorage();
    if (storage?.excludedUrls) {
      storage.excludedUrls.add(url);
    }
  }, [getStorage]);

  const scanAndShortenNewLinks = useCallback(() => {
    const storage = getStorage();
    if (storage?.scan) {
      storage.scan();
    }
  }, [getStorage]);

  const applyLinkMarksToRawUrls = useCallback(() => {
    // Handled by extension
  }, []);

  // Clear all URL tracking sets when parent signals (e.g., contact change, message sent)
  useEffect(() => {
    if (typeof excludedClearTrigger !== 'undefined' && excludedClearTrigger > 0) {
      const storage = getStorage();
      if (storage) {
        storage.excludedUrls?.clear();
        storage.processedShortened?.clear();
        storage.processingUrls?.clear();
        storage.isProcessing = false;
        if (onProcessingChange) {
          onProcessingChange(false);
        }
      }
    }
  }, [excludedClearTrigger, getStorage, onProcessingChange]);

  // Sync isProcessing from extension storage
  useEffect(() => {
    const storage = getStorage();
    if (storage) {
      const updateProcessing = () => {
        const currentProcessing = storage.isProcessing || false;
        setIsProcessing(currentProcessing);
        if (onProcessingChange) {
          onProcessingChange(currentProcessing);
        }
      };
      updateProcessing();
    }
  }, [getStorage, onProcessingChange]);

  return {
    scanAndShortenNewLinks,
    applyLinkMarksToRawUrls,
    addExcludedUrl,
    isProcessing,
  };
}